import { Navigate, useLocation } from 'react-router-dom';
import { isDashboardTokenUsable, useAuthStore } from '@/store/authStore';
import { useTenantContext } from '@/hooks/useTenantContext';
import { useProviderCapabilities } from '@/hooks/useProviderCapabilities';
import { isProviderPathAllowed } from '@/lib/provider-capabilities.mjs';
import { useEnabledModules } from '@/hooks/useEnabledModules';
import { PageLoader } from '@/components/ui';

// Auth guard component
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuthStore();
  const token = localStorage.getItem('maksab_access_token');
  if (!isDashboardTokenUsable(token) || !isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  return <>{children}</>;
}

// Guest route (redirect if authenticated)
export function GuestRoute({ children }: { children: React.ReactNode }) {
  const token = localStorage.getItem('maksab_access_token');
  if (isDashboardTokenUsable(token)) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// SuperAdmin only route guard
export function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin } = useTenantContext();
  if (!isSuperAdmin) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}

// ProviderAdmin only route guard
export function ProviderAdminRoute({ children }: { children: React.ReactNode }) {
  const { role, isAuthenticated } = useAuthStore();
  const { providerAdminAllowed, ready } = useEnabledModules();
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (role !== 'provideradmin') return <Navigate to="/" replace />;
  if (!ready) return <PageLoader />;
  if (!providerAdminAllowed) return <Navigate to="/login?reason=provider-portal-disabled" replace />;
  return <>{children}</>;
}

/** Applies the sidebar's additive capability policy to direct URLs. */
export function ProviderCapabilityRoute({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const providerCapabilities = useProviderCapabilities();
  const path = location.pathname;

  // Dashboard and settings stay reachable to complete initial configuration.
  if (isProviderPathAllowed(path, {})) return <>{children}</>;
  if (!providerCapabilities.ready && !providerCapabilities.isError) return <PageLoader />;
  if (!providerCapabilities.isPathAllowed(path)) return <Navigate to="/business" replace />;
  return <>{children}</>;
}

// Tenant route guard - requires active tenant context
// SuperAdmin must impersonate a tenant first, TenantAdmin always has context
export function TenantRoute({ children }: { children: React.ReactNode }) {
  const { activeTenantId } = useTenantContext();
  const { role } = useAuthStore();
  // Provider-admins are confined to /business/* (ProviderAdminRoute).
  // useTenantContext maps provideradmin -> tenantadmin mode for data
  // scoping, but they must NOT operate the tenant marketplace console
  // (orders/customers/providers/catalog/config). Gate on the raw role
  // so the mode mapping is untouched.
  if (role === 'provideradmin') {
    return <Navigate to="/business" replace />;
  }
  if (role === 'superadmin' && !activeTenantId) {
    return <Navigate to="/tenants" replace />;
  }
  const isTenantRole = role === 'tenantadmin' || role === 'support';
  const isScopedSuperAdmin = role === 'superadmin' && activeTenantId != null;
  if ((!isTenantRole && !isScopedSuperAdmin) || activeTenantId == null) {
    return <div role="alert">Tenant access is unavailable.</div>;
  }
  return <>{children}</>;
}

// Notification administration is limited to SuperAdmin and TenantAdmin.
// Support and ProviderAdmin must not edit delivery gateways or templates.
export function AdminOrTenantRoute({ children }: { children: React.ReactNode }) {
  const { activeTenantId } = useTenantContext();
  const { role } = useAuthStore();

  if (role === 'provideradmin') {
    return <Navigate to="/business" replace />;
  }

  if (role === 'superadmin') {
    return <>{children}</>;
  }

  if (role !== 'tenantadmin' || activeTenantId == null) {
    return <Navigate to="/" replace />;
  }

  return <>{children}</>;
}
